import { makeStyles } from '@mui/styles'

const useGlobalStyles = makeStyles(theme => ({
  flex: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Row that holds a form's submit button: aligned with the fields above it
  // rather than flush against the card edge.
  formActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8
  },
  input: {
    backgroundColor: '#FAFAFB',
    margin: '6px 0 0 0',
    height: 44,
    width: '100%',
    fontSize: 14,
    color: theme.palette.text.primary,
    padding: '5px 14px',
    borderRadius: 12,
    boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.07)',
    border: `1px solid ${theme.palette.divider}`,
    transition:
      'border-color .15s ease, box-shadow .15s ease, background-color .15s ease',
    '&:hover': {
      borderColor: theme.palette.grey[400]
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.warning.darkest}`
    }
  },
  // Multi-line counterpart to `input`. That one pins height to 42px for a
  // single line; a textarea inside it overflows the box instead of growing,
  // so the text spills out above the field.
  textArea: {
    backgroundColor: '#FAFAFB',
    margin: '6px 0 0 0',
    width: '100%',
    fontSize: 14,
    color: theme.palette.text.primary,
    padding: '12px 14px',
    borderRadius: 12,
    boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.07)',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      borderColor: theme.palette.grey[400]
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.warning.darkest}`
    }
  },
  inputError: {
    border: `1px solid ${theme.palette.error.main}`,
    boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.12)'
  },
  inputSuccess: {
    border: `1px solid ${theme.palette.success.main}`,
    boxShadow: '0 0 0 3px rgba(22, 163, 74, 0.12)'
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    backgroundImage:
      'linear-gradient(145deg, #FF9458 0%, #F4661F 55%, #D9500F 100%)',
    minHeight: 44,
    borderRadius: 12,
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none',
    border: 'none',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.12), 0 8px 18px -6px rgba(244,102,31,0.55)',
    margin: '24px 0 0 0',
    padding: '8px 24px',
    transition: 'transform .12s ease, box-shadow .12s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main3,
      color: theme.palette.common.white,
      transform: 'translateY(-1px)',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.12), 0 12px 22px -8px rgba(244,102,31,0.6)'
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow:
        'inset 0 2px 4px rgba(0,0,0,0.18), 0 3px 8px -4px rgba(244,102,31,0.5)'
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[200],
      backgroundImage: 'none',
      boxShadow: 'none',
      color: theme.palette.text.disabled
    }
  },
  button100: {
    width: '90%',
    backgroundColor: theme.palette.primary.main,
    backgroundImage:
      'linear-gradient(145deg, #FF9458 0%, #F4661F 55%, #D9500F 100%)',
    minHeight: 40,
    borderRadius: 12,
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none',
    border: 'none',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.12), 0 8px 18px -6px rgba(244,102,31,0.55)',
    margin: '24px 0 0 0',
    padding: '8px 24px',
    transition: 'transform .12s ease, box-shadow .12s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main3,
      color: theme.palette.common.white,
      transform: 'translateY(-1px)',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.12), 0 12px 22px -8px rgba(244,102,31,0.6)'
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow:
        'inset 0 2px 4px rgba(0,0,0,0.18), 0 3px 8px -4px rgba(244,102,31,0.5)'
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[200],
      backgroundImage: 'none',
      boxShadow: 'none',
      color: theme.palette.text.disabled
    }
  },
  selectInput: {
    height: 42,
    width: '100%',
    backgroundColor: '#FAFAFB',
    borderRadius: 12,
    boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.07)',
    border: `1px solid ${theme.palette.divider}`
  },
  // Generic surface used by the "add / edit" panels that sit above each
  // listing table.
  timing: {
    backgroundColor: theme.palette.common.white,
    margin: '0 0 24px 0',
    borderRadius: 18,
    border: '1px solid rgba(16,24,40,0.07)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 2px rgba(16,24,40,0.06), 0 10px 28px -12px rgba(16,24,40,0.18)',
    backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #FBFBFC 100%)',
    textAlign: 'center',
    overflow: 'hidden'
  },
  timingHeader: {
    backgroundColor: theme.palette.common.white,
    backgroundImage:
      'linear-gradient(180deg, rgba(255,241,233,0.75) 0%, rgba(255,255,255,0) 100%)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: '16px 16px 0 0',
    fontWeight: 700,
    fontSize: 16,
    padding: '16px 20px',
    color: theme.palette.text.primary
  },
  dayComponent: {
    padding: 5,
    borderRadius: 10,
    marginTop: 5,
    marginBottom: 5
  },
  day: {
    color: theme.palette.text.primary,
    borderRadius: 10
  },
  openBtn: {
    borderRadius: 999,
    padding: '2px 12px',
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: theme.palette.success.light,
    color: '#15803D'
  },
  closeBtn: {
    borderRadius: 999,
    padding: '2px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.grey[100]
  },
  mb: {
    marginBottom: 20
  },
  alertSuccess: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    borderRadius: 12,
    paddingTop: 0,
    paddingBottom: 0
  },
  alertError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    borderRadius: 12,
    paddingTop: 0,
    paddingBottom: 0
  }
}))

export default useGlobalStyles
